import _ from 'lodash';
import velocity from 'velocity-animate';
import classNames from 'classnames';
import breakpoints from 'datasetLandingPage/lib/breakpoints';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import {
  setContactFormField,
  submitContactForm,
  resetContactForm,
} from 'datasetLandingPage/actions/contactForm';
import TextInput from './TextInput';
import { VALID_EMAIL_REGEX } from 'common/http/constants';
import { I18n } from 'common/i18n';
import { Dispatcher } from 'datasetLandingPage/lib/types';
import { ContactFormField, ContactFormFields, ContactFormStatus } from './types/contactForm';

const animationDuration = 300;
const animationEasing = [0.645, 0.045, 0.355, 1];

const t = (str: string) => I18n.t(str, { scope: 'dataset_landing_page.contact_dataset_owner_modal' });

export interface ContactFormProps {
  fields: ContactFormFields;
  resetForm: () => void;
  onClickSend: () => void;
  onChangeFormField: (fieldName: string, value: ContactFormField) => void;
  status: ContactFormStatus;
}

interface ContactFormState {
  errors: string[];
}

export class ContactForm extends Component<ContactFormProps, ContactFormState> {
  constructor(props: ContactFormProps) {
    super(props);

    this.state = {
      errors: [],
    };

    _.bindAll(this, 'onClickSend', 'onFieldChange', 'cleanUpAfterClose');
  }

  componentDidUpdate() {
    const { status } = this.props;

    if (_.isEqual(status, 'success') || _.isEqual(status, 'failure')) {
      this.closeModal();
    }
  }

  onClickSend(event: React.SyntheticEvent<HTMLElement>) {
    event.preventDefault();

    const { onClickSend } = this.props;
    const errors = this.validateForm();

    if (_.some(errors)) {
      const invalidField: HTMLElement | null = (ReactDOM.findDOMNode(this) as Element).querySelector(
        '[aria-invalid="true"]',
      );
      if (invalidField) {
        invalidField.focus();
      }
      this.setState({ errors });
    } else {
      onClickSend();
    }
  }

  onFieldChange(event: React.SyntheticEvent<HTMLElement>) {
    const { errors } = this.state;
    if (errors && errors.length) {
      this.setState({
        errors: [],
      });
    }
    const { id, value } = event.target as HTMLInputElement;
    const isInvalid = _.isEmpty(value) || (_.isEqual(id, 'email') && !VALID_EMAIL_REGEX.test(value));

    this.props.onChangeFormField(id, {
      value,
      invalid: isInvalid,
    });
  }

  validateForm() {
    const { fields } = this.props;

    return _.reduce(
      fields,
      (result: string[], field, id) => {
        if (field.invalid) {
          if (!_.isEqual(id, 'email') || _.isEmpty(field.value)) {
            result.push(t(`error_empty_${id}`));
          } else if (_.isEqual(id, 'email')) {
            result.push(t('error_invalid_email'));
          }
        }

        return result;
      },
      [],
    );
  }

  cleanUpAfterClose() {
    const { resetForm } = this.props;
    const element = ReactDOM.findDOMNode(this) as Element;

    element.classList.add('modal-hidden');
    document.body.classList.remove('modal-open');

    const container = element.querySelector('.modal-container') as HTMLElement | null;

    if (container) {
      // these attributes are set by velocity's transitions
      container.style.left = '';
      container.style.display = '';
      container.style.opacity = '';
    }

    resetForm();
    this.setState({ errors: [] });
  }

  closeModal() {
    const element = (ReactDOM.findDOMNode(this) as Element).querySelector('.modal-container');
    const windowWidth = document.body.offsetWidth;
    const isMobile = windowWidth <= breakpoints.mobile;

    if (isMobile) {
      velocity(
        element,
        {
          left: windowWidth,
        },
        {
          duration: animationDuration,
          easing: animationEasing,
          complete: this.cleanUpAfterClose,
        },
      );
    } else {
      velocity(element, 'fadeOut', {
        duration: 500,
        complete: this.cleanUpAfterClose,
      });
    }
  }

  renderContactForm() {
    const { fields, status } = this.props;

    const isSending = _.isEqual(status, 'sending');
    const sendButtonClasses = classNames('btn btn-primary btn-sm', {
      sending: isSending,
    });

    const sendButtonContent = isSending ? (
      <span aria-label={t('sending')} className="spinner-default" />
    ) : (
      t('send')
    );

    return (
      <div role="dialog" aria-labelledby="contact-owner-title">
        <header className="modal-header">
          <h2 id="contact-owner-title" className="h5 modal-header-title">
            {t('title')}
          </h2>
          <button
            aria-label={I18n.t('dataset_landing_page.close')}
            className="btn btn-transparent modal-header-dismiss"
            onClick={this.cleanUpAfterClose}
            data-modal-dismiss
          >
            <span className="icon-close-2"></span>
          </button>
        </header>

        <section className="modal-content">
          <form id="contact" onSubmit={this.onClickSend}>
            <p className="small">{t('description')}</p>
            {this.renderErrorMessages()}
            <TextInput
              field={fields.subject}
              label={t('subject')}
              name="subject"
              onChange={this.onFieldChange}
            />
            <TextInput
              field={fields.message}
              label={t('message')}
              name="message"
              isTextArea={true}
              onChange={this.onFieldChange}
            />
            <TextInput
              description={t('email_description')}
              field={fields.email}
              label={t('email')}
              name="email"
              onChange={this.onFieldChange}
            />
          </form>
        </section>

        <footer className="modal-actions">
          <button
            id="contact-form-cancel"
            className="btn btn-default btn-sm"
            data-modal-dismiss
            disabled={isSending}
            onClick={this.cleanUpAfterClose}
          >
            {I18n.t('dataset_landing_page.cancel')}
          </button>
          <button
            type="submit"
            id="contact-form-send"
            className={sendButtonClasses}
            disabled={isSending}
            onClick={this.onClickSend}
          >
            {sendButtonContent}
          </button>
        </footer>
      </div>
    );
  }

  renderErrorMessages() {
    const { errors } = this.state;

    if (_.some(errors)) {
      const messages = _.map(errors, function (error, i) {
        return <p key={i}>{error}</p>;
      });

      return (
        <section className="modal-content">
          <div className="alert error validation">{messages}</div>
        </section>
      );
    }
  }

  render() {
    return (
      <div id="contact-form" className="modal modal-overlay modal-hidden" data-modal-dismiss>
        <div className="modal-container">{this.renderContactForm()}</div>
      </div>
    );
  }
}

function mapStateToProps(state: { contactForm: ContactFormState }) {
  return state.contactForm;
}

function mapDispatchToProps(dispatch: Dispatcher) {
  return {
    onChangeFormField(fieldName: string, value: ContactFormField) {
      dispatch(setContactFormField(fieldName, value));
    },

    onClickSend() {
      dispatch(submitContactForm());
    },

    resetForm() {
      dispatch(resetContactForm());
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ContactForm);
